import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import LanguageSelector from '../../../components/common/LanguageSelector';
import CityIntroduction from '../../../components/feature/CityIntroduction';
import { getCityData, getCitiesByCountry, getCityCountryCode, urlSlugToCityName, cityNameToUrlSlug } from '../../../utils/cityData';
import { getSEOConfig, updateSEOTags } from '../../../i18n/utils/seo';
import type { SupportedLanguage } from '../../../i18n/utils/language';

export default function CityTime() {
  console.log('=== CityTime Component Loaded ===');
  console.log('Current URL:', window.location.pathname);

  const { citySlug } = useParams<{ citySlug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['worldclock', 'common']);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  console.log('URL param citySlug:', citySlug);
  console.log('Raw URL:', window.location.href);
  console.log('useParams result:', useParams());

  // 从URL参数解析城市名
  // 如果 slug 是 "time-in-chicago"，提取出 "chicago"
  let city = citySlug || '';
  if (city.startsWith('time-in-')) {
    city = city.replace('time-in-', '');
  }
  console.log('Extracted city slug:', city);

  const cityName = city ? urlSlugToCityName(city) : '';
  console.log('After urlSlugToCityName, cityName:', cityName);

  const cityData = getCityData(cityName);

  console.log('Parsed cityName:', cityName);
  console.log('Found cityData:', cityData);
  console.log('cityData is null?', cityData === null);

  // 如果找不到城市数据，重定向到世界时钟页面
  useEffect(() => {
    if (citySlug && !cityData) {
      console.log('City not found, redirecting to worldclock');
      navigate('/worldclock');
      return;
    }
  }, [citySlug, cityData, navigate]);

  // 获取当前城市所在国家的热门城市
  const popularCities = useMemo(() => {
    if (!cityData) return [];
    const countryCode = getCityCountryCode(cityData.name);
    if (!countryCode) return [];
    return getCitiesByCountry(countryCode).slice(0, 50);
  }, [cityData]);

  // 用户评价数据
  const [randomizedReviews, setRandomizedReviews] = useState<Array<{
    id: number;
    avatar: string;
    name: string;
    rating: number;
    review: string;
  }>>([]);

  useEffect(() => {
    const userReviews = [
      {
        id: 1,
        avatar: "/images/avatars/Global Business.jpg",
        name: t('user.globalBusiness'),
        rating: 5,
        review: t('reviews.globalBusiness')
      },
      {
        id: 2,
        avatar: "/images/avatars/Remote Developer.jpg",
        name: t('user.remoteTeam'),
        rating: 5,
        review: t('reviews.remoteTeam')
      },
      {
        id: 3,
        avatar: "/images/avatars/Travel Blogger.jpg",
        name: t('user.travelBlogger'),
        rating: 4,
        review: t('reviews.travelBlogger')
      },
      {
        id: 4,
        avatar: "/images/avatars/International Family.jpg",
        name: t('user.internationalFamily'),
        rating: 5,
        review: t('reviews.internationalFamily')
      },
      {
        id: 5,
        avatar: "/images/avatars/Manager Zhang.jpg",
        name: t('user.managerZhang'),
        rating: 5,
        review: t('reviews.managerZhang')
      }
    ];

    const shuffled = [...userReviews].sort(() => Math.random() - 0.5);
    setRandomizedReviews(shuffled);
  }, [t, i18n.language]);

  useEffect(() => {
    // 更新SEO配置
    const currentLang = i18n.language as SupportedLanguage;

    // 使用动态城市页面的SEO配置
    if (cityData) {
      // 根据当前语言选择显示名称
      const cityDisplayName = i18n.language === 'zh' ? cityData.nameZh : cityData.nameEn;
      const countryDisplayName = i18n.language === 'zh' ? (cityData.countryZh || cityData.country) : cityData.country;

      const seoConfig = getSEOConfig('city', currentLang, {
        cityName: cityDisplayName,
        countryName: countryDisplayName,
        timezone: cityData.timezone
      });
      updateSEOTags(seoConfig, currentLang);
    }
  }, [i18n.language, cityData]);

  // 定时器更新时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (timezone: string) => {
    if (!timezone) {
      timezone = 'UTC';
    }

    try {
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.warn(`Invalid timezone format: ${timezone}`);
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: 'UTC',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  };

  const formatDate = useCallback((timezone: string) => {
    if (!timezone) {
      timezone = 'UTC';
    }

    // 根据语言选择locale
    const localeMap: Record<string, string> = {
      'zh': 'zh-CN',
      'ja': 'ja-JP',
      'hi': 'hi-IN',
      'id': 'id-ID',
      'en': 'en-US'
    };

    const locale = localeMap[i18n.language] || 'en-US';

    try {
      return currentTime.toLocaleDateString(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      });
    } catch (error) {
      console.warn(`Invalid timezone format: ${timezone}`);
      return currentTime.toLocaleDateString(locale, {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      });
    }
  }, [currentTime, i18n.language]);

  const getTimeOffset = (timezone: string) => {
    if (!timezone) {
      timezone = 'UTC';
    }

    try {
      const offset = getTimezoneOffset(timezone);
      return offset >= 0 ? `UTC+${offset}` : `UTC${offset}`;
    } catch (error) {
      return 'UTC+0';
    }
  };

  const getTimezoneOffset = (timezone: string) => {
    try {
      const now = new Date();
      const utcTime = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
      const targetTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
      return (targetTime.getTime() - utcTime.getTime()) / (1000 * 60 * 60);
    } catch (error) {
      return 0;
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleFullscreenKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFullscreen(false);
    }
  };

  const handleCityClick = (clickedCity: any) => {
    const citySlug = cityNameToUrlSlug(clickedCity.nameEn);
    navigate(`/worldclock/time-in-${citySlug}`);
  };

  // 如果城市数据不存在，显示加载或错误状态
  if (!cityData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t('cityNotFound', { defaultValue: 'City not found' })}
            </h2>
            <button
              onClick={() => navigate('/worldclock')}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {t('backToWorldClock', { defaultValue: 'Back to World Clock' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cityDisplayName = i18n.language === 'zh' ? cityData.nameZh : cityData.nameEn;

  // 全屏模式
  if (isFullscreen) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
        onClick={toggleFullscreen}
        onKeyDown={handleFullscreenKeyPress}
        tabIndex={0}
      >
        <div className="text-center" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={toggleFullscreen}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          
          <div className="mb-12">
            <div className="text-[3.75rem] md:text-[8rem] font-mono font-[700] text-blue-400 mb-8">
              {formatTime(cityData.timezone)}
            </div>
            
            <div className="space-y-4 text-white">
              <div className="text-2xl">
                {formatDate(cityData.timezone)}
              </div>
              <div className="text-lg text-gray-300">
                {cityDisplayName}: {cityData.timezone} ({getTimeOffset(cityData.timezone)})
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back button */}
        <div className="mb-4 md:mb-6">
          <button
            onClick={() => navigate('/worldclock')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            {t('common:button.back')}
          </button>
        </div>

        {/* Current Time Display */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <div className="text-center">
            <div className="flex justify-between items-start mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                {t('timeIn', { defaultValue: 'Time in' })} {cityDisplayName}
              </h2>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleFullscreen}
                  className="text-gray-600 hover:text-blue-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex items-center space-x-2"
                  title="Fullscreen"
                >
                  <span className="hidden md:block text-sm font-medium">{t('common:label.fullscreen')}</span>
                  <i className="ri-picture-in-picture-2-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="mb-8 md:mb-10">
              <div className="flex items-center justify-center mb-6 md:mb-8">
                <div className="text-[3.75rem] md:text-8xl font-mono font-bold text-blue-600">
                  {formatTime(cityData.timezone)}
                </div>
              </div>
              
              <div className="space-y-2 md:space-y-3">
                <div className="text-lg md:text-xl text-gray-600">
                  {formatDate(cityData.timezone)}
                </div>
                <div className="text-sm md:text-base text-gray-500">
                  {cityData.timezone} ({getTimeOffset(cityData.timezone)})
                </div>
                <div className="text-sm text-gray-400">
                  {cityData.flag} {cityData.country}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* City Introduction */}
        <CityIntroduction 
          cityName={cityDisplayName} 
          timezone={cityData.timezone}
          className="mb-6 md:mb-8" 
        />

        {/* Popular Cities in the Same Country */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6">
            {t('popularCitiesIn', { defaultValue: 'Popular Cities in' })} {cityData.country}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularCities
              .filter((popularCity) => {
                // 过滤掉当前城市
                const isCurrentCity = popularCity.name === cityData.name ||
                                    popularCity.nameEn === cityData.nameEn;
                return !isCurrentCity;
              })
              .map((popularCity) => {
                const displayName = i18n.language === 'zh' ? popularCity.nameZh : popularCity.nameEn;

                return (
                  <div
                    key={popularCity.timezone + popularCity.name}
                    className="bg-gray-50 rounded-xl p-6 transition-colors cursor-pointer hover:bg-gray-100"
                    onClick={() => handleCityClick(popularCity)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">{popularCity.flag}</span>
                        <span className="font-semibold text-gray-800 text-lg">{displayName}</span>
                      </div>
                      <span className="text-xs text-gray-500">{getTimeOffset(popularCity.timezone)}</span>
                    </div>
                    <div className="text-3xl font-mono font-bold text-gray-900 mb-1">
                      {formatTime(popularCity.timezone)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatDate(popularCity.timezone)}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('howto.title')}</h3>
          <div className="space-y-3 md:space-y-4 text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">1</div>
              <p className="text-sm md:text-base">{t('howto.step1')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">2</div>
              <p className="text-sm md:text-base">{t('howto.step2')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">3</div>
              <p className="text-sm md:text-base">{t('howto.step3')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">4</div>
              <p className="text-sm md:text-base">{t('howto.step4')}</p>
            </div>
          </div>
        </div>

        {/* User Reviews */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('reviews.title')}</h3>
          <div className="space-y-4 md:space-y-6">
            {randomizedReviews.slice(0, 3).map((review) => (
              <div key={review.id} className="flex items-start space-x-4">
                <img 
                  src={review.avatar} 
                  alt={review.name}
                  className="w-12 h-12 rounded-full bg-gray-300 flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/avatars/default.png';
                  }}
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm md:text-base">{review.name}</h4>
                    <div className="flex space-x-0.5">
                      {[...Array(5)].map((_, i) => (
                        <i 
                          key={i} 
                          className={`ri-star${i < review.rating ? '-fill' : '-line'} text-yellow-400 text-sm`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">{review.review}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-100 text-gray-800 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
            <div className="lg:w-2/3">
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('common:footer.about')}</h4>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {t('common:footer.aboutDesc')}
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <i className="ri-timer-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.timer')}</span> - {t('common:footer.timerDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-play-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.stopwatch')}</span> - {t('common:footer.stopwatchDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-purple-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.worldclock')}</span> - {t('common:footer.worldClockDesc')}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <a href="/terms" className="hover:text-gray-700 transition-colors">{t('common:footer.termsOfService')}</a>
                  <span>|</span>
                  <a href="#" className="hover:text-gray-700 transition-colors">{t('common:footer.privacy')}</a>
                  <span>|</span>
                  <a href="mailto:mu9347mu@gmail.com?subject=Contact Us - timer.today&body=Hello timer.today team,%0D%0A%0D%0AI would like to..." className="hover:text-gray-700 transition-colors">{t('common:footer.contactUs')}</a>
                  <span>|</span>
                  <span>{t('common:footer.copyright')}</span>
                  <span className="lg:hidden">|</span>
                  <div className="lg:hidden">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:flex w-px bg-gray-300 mx-6 h-40 self-center"></div>

            <div className="hidden lg:block lg:w-1/3">
              <div className="space-y-3 text-sm text-gray-600 mt-12">
                <p>world clock time zones international time </p>
                <p>世界時計 タイムゾーン 国際時間 今何時</p>
                <p>reloj mundial zonas horarias tiempo internacional qué hora</p>
                <p>Weltzeituhr Zeitzonen internationale Zeit wie spät</p>
                <p>мировые часы часовые пояса международное время который час</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}